import React, { useState } from 'react'
import { Badge, BottomNavigation, BottomNavigationAction, Box, IconButton, Tooltip } from '@mui/material'
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded'
import { TAB_ICONS, TabIconKey } from '../dashboard/tabs'
import { TOUR_ANCHORS } from '../tour/anchors'
import { advancedColors } from '../theme'
import ManageTabsDialog, { TabWithContent } from './ManageTabsDialog'

type Props = {
  tabs: TabWithContent[]
  active: string
  editMode: boolean
  onChange: (id: string) => void
  onAddTab: (label: string, icon: TabIconKey) => void
  onRemoveTab: (id: string) => void
  onSetVisible: (id: string, visible: boolean) => void
  onMoveTo: (from: number, to: number) => void
  // Tab id → why it needs attention, e.g. "needs review".
  attention?: Record<string, string>
}

const PatientTabSwitcher = ({
  tabs,
  active,
  editMode,
  onChange,
  onAddTab,
  onRemoveTab,
  onSetVisible,
  onMoveTo,
  attention = {},
}: Props) => {
  const [manageOpen, setManageOpen] = useState(false)
  const visibleTabs = tabs.filter((tab) => tab.visible)

  return (
    <Box
      data-tour={TOUR_ANCHORS.tabBar}
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'stretch',
        borderTop: `1px solid ${advancedColors.line}`,
        backgroundColor: advancedColors.surface,
      }}
    >
      <BottomNavigation
        showLabels
        value={active}
        onChange={(_, value: string) => onChange(value)}
        sx={{ flex: 1, backgroundColor: 'transparent', height: 64 }}
      >
        {visibleTabs.map((tab) => {
          const Icon = TAB_ICONS[tab.icon]
          const reason = attention[tab.id]

          return (
            <BottomNavigationAction
              key={tab.id}
              value={tab.id}
              data-tour={TOUR_ANCHORS.tab(tab.id)}
              label={tab.label}
              aria-label={reason ? `${tab.label}, ${reason}` : undefined}
              icon={
                <Badge variant="dot" color="error" invisible={!reason}>
                  <Icon />
                </Badge>
              }
              sx={{ color: advancedColors.inkMuted, '&.Mui-selected': { color: advancedColors.green } }}
            />
          )
        })}
      </BottomNavigation>

      {editMode && (
        <Box sx={{ display: 'flex', alignItems: 'center', mx: 1.5, gap: 0.5 }}>
          <Tooltip title="Manage tabs">
            <IconButton
              aria-label="Manage tabs"
              onClick={() => setManageOpen(true)}
              sx={{ color: advancedColors.inkMuted }}
            >
              <ListAltRoundedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <ManageTabsDialog
        tabs={tabs}
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        onAddTab={onAddTab}
        onRemoveTab={onRemoveTab}
        onSetVisible={onSetVisible}
        onMoveTo={onMoveTo}
      />
    </Box>
  )
}

export default PatientTabSwitcher
